import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaCommentDots } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import FloatingList from "Components/FloatingList";
import Badge from "Components/UI/Badge";
import DocumentRequestSection from "Components/PatentDesign/DocumentRequestSection";
import DocumentSection from "Components/PatentDesign/DocumentSection";
import NoticeSection from "Components/PatentDesign/NoticeSection";
import PaymentSection from "Components/PatentDesign/PaymentSection";
import ProgressSection from "Components/PatentDesign/ProgressSection";
import RequestInfoSection from "Components/PatentDesign/RequestInfoSection";
import { getPatentDesignRequestDetail } from "JS/API";
import { errorList } from "JS/array";
import { catchError, commonModalSetting } from "JS/common";
import { commonStyles, designTokens, detailPageStyles } from "utils/designSystem";
import {
  PATENT_DESIGN_STATUS,
  PATENT_DESIGN_STATUS_STYLE,
  requestTypeLabel,
} from "utils/patentDesign";

const NOT_FOUND_ACTION_MESSAGE = "이미 삭제되었거나 찾을 수 없는 항목입니다.";
const GENERIC_FAILURE_MESSAGE = "처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";

const styles = {
  container: {
    padding: designTokens.spacing.xxl,
    paddingRight: "380px",
    backgroundColor: designTokens.colors.background,
    minHeight: "100vh",
  },
  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing.xs,
    border: "none",
    background: "none",
    color: designTokens.colors.text.tertiary,
    cursor: "pointer",
    fontSize: "14px",
    padding: 0,
    marginBottom: designTokens.spacing.lg,
  },
  sections: { border: "none", margin: 0, padding: 0, minWidth: 0 },
  title: { ...detailPageStyles.title, fontSize: "24px", wordBreak: "break-all" },
  notFound: {
    ...commonStyles.card,
    textAlign: "center",
    color: designTokens.colors.text.tertiary,
  },
  commentButton: {
    position: "fixed",
    right: "24px",
    bottom: "24px",
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: designTokens.colors.primary,
    color: "#ffffff",
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 997,
    boxShadow: designTokens.shadows.lg,
  },
};

const PatentDesignRequestDetail = () => {
  const { id } = useParams();
  const requestId = Number(id) || null;
  const navigate = useNavigate();
  const location = useLocation();
  const latestIdRef = useRef(requestId);
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  latestIdRef.current = requestId;

  const showAlert = useCallback(
    (message) => commonModalSetting(setAlertBox, true, "alert", message),
    []
  );

  const fetchDetail = useCallback(async () => {
    if (!requestId) {
      setNotFound(true);
      return;
    }
    setIsReloading(true);
    let result;
    try {
      result = await getPatentDesignRequestDetail(requestId);
    } catch {
      result = undefined;
    }
    if (latestIdRef.current !== requestId) return;
    setIsReloading(false);
    if (result && typeof result === "object") {
      setDetail(result.data?.data || null);
      setNotFound(false);
    } else if (result === "notFound") {
      setDetail(null);
      setNotFound(true);
    } else if (result === "renderErrorPage" || errorList[result]) {
      await catchError(result, navigate, setAlertBox, setAlert);
    } else {
      showAlert("신청을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
    }
  }, [requestId, navigate, showAlert]);

  useEffect(() => {
    setDetail(null);
    setNotFound(false);
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    document.title = "마크픽 관리자 > 특허·디자인 신청 상세";
  }, []);

  const handleError = useCallback(
    (result) => {
      if (result === "notFound") {
        showAlert(NOT_FOUND_ACTION_MESSAGE);
        fetchDetail();
        return;
      }
      if (result === "renderErrorPage" || errorList[result]) {
        catchError(result, navigate, setAlertBox, setAlert);
        return;
      }
      showAlert(GENERIC_FAILURE_MESSAGE);
    },
    [fetchDetail, navigate, showAlert]
  );

  const goList = () => {
    if (location.state?.fromList) navigate(-1);
    else navigate("/patent-design");
  };

  const sectionProps = detail && {
    detail,
    onReload: fetchDetail,
    onAlert: showAlert,
    onError: handleError,
  };

  return (
    <div className="patent-design-detail" style={styles.container}>
      <button type="button" style={styles.backButton} onClick={goList}>
        <FaArrowLeft size={12} />
        목록으로
      </button>

      {notFound && <div style={styles.notFound}>신청을 찾을 수 없습니다.</div>}

      {detail && (
        <>
          <header style={detailPageStyles.header}>
            <h1 style={styles.title}>
              No.{detail.id} · {detail.title}
              <Badge style={PATENT_DESIGN_STATUS_STYLE[detail.status]}>
                {PATENT_DESIGN_STATUS[detail.status] || detail.status}
              </Badge>
              <Badge>{requestTypeLabel(detail.request_type)}</Badge>
            </h1>
          </header>

          <fieldset style={styles.sections} disabled={isReloading} aria-busy={isReloading}>
            <RequestInfoSection {...sectionProps} />
            <ProgressSection {...sectionProps} />
            <NoticeSection {...sectionProps} />
            <DocumentSection {...sectionProps} />
            <DocumentRequestSection {...sectionProps} />
            <PaymentSection {...sectionProps} />
          </fieldset>

          <button
            type="button"
            className="patent-design-comment-button"
            aria-label="내부 코멘트 열기"
            style={styles.commentButton}
            onClick={() => setIsCommentOpen(true)}
          >
            <FaCommentDots size={22} />
          </button>
          <FloatingList
            type="PATENT_DESIGN"
            id={detail.id}
            isOpen={isCommentOpen}
            onClose={() => setIsCommentOpen(false)}
          />
        </>
      )}

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
          }}
        />
      )}

      <style>{`
        @media (max-width: 1024px) {
          .patent-design-detail { padding-right: ${designTokens.spacing.xxl} !important; }
          .patent-design-comment-button { display: inline-flex !important; }
        }
      `}</style>
    </div>
  );
};

export default PatentDesignRequestDetail;
